import { clsx } from 'clsx';
import { type ReactNode, useEffect, useRef } from 'react';
import { useQuality } from '@/lib/quality';
import { magnetOffset } from './geometry';
import { damp } from './math';

export interface MagnetProps {
  children: ReactNode;
  /** Fraction of the pointer offset the element follows. Default 0.25. */
  strength?: number;
  /** Pointer distance (px) from the element's edge at which the pull starts. Default 80. */
  radius?: number;
  className?: string;
}

const FINE_POINTER = '(hover: hover) and (pointer: fine)';
/** Spring-back time: the remaining offset halves every ~70 ms (≈ 400 ms to settle). */
const HALF_LIFE_MS = 70;

/**
 * Magnetic hover (full tier, fine pointers only): the wrapped control leans towards the cursor by up
 * to `strength` × the offset and springs back when it leaves. Transform-only; never changes layout,
 * hit-testing or keyboard behaviour. On touch, `lite`/`minimal` tiers it renders a plain wrapper.
 */
export function Magnetic({ children, strength = 0.25, radius = 80, className }: MagnetProps) {
  const tier = useQuality();
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || tier !== 'full' || !window.matchMedia(FINE_POINTER).matches) return undefined;

    const current = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let pointer: { x: number; y: number } | null = null;
    let frame = 0;
    let last = 0;

    const tick = (now: number): void => {
      const dt = last === 0 ? 16 : Math.min(64, now - last);
      last = now;
      if (pointer) {
        // the measured rect already includes our own translation: aim from the resting position
        const rect = element.getBoundingClientRect();
        const rest = {
          left: rect.left - current.x,
          top: rect.top - current.y,
          width: rect.width,
          height: rect.height,
        };
        const next = magnetOffset(rest, pointer, strength, radius);
        target.x = next.x;
        target.y = next.y;
        pointer = null;
      }
      current.x = damp(current.x, target.x, HALF_LIFE_MS, dt);
      current.y = damp(current.y, target.y, HALF_LIFE_MS, dt);
      const done = Math.abs(current.x - target.x) < 0.05 && Math.abs(current.y - target.y) < 0.05;
      if (done) {
        current.x = target.x;
        current.y = target.y;
      }
      element.style.transform =
        current.x === 0 && current.y === 0
          ? ''
          : `translate3d(${current.x.toFixed(2)}px, ${current.y.toFixed(2)}px, 0)`;
      if (done) {
        frame = 0;
        last = 0; // the loop sleeps until the pointer moves again
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };

    const onMove = (event: PointerEvent): void => {
      pointer = { x: event.clientX, y: event.clientY };
      if (frame === 0) frame = window.requestAnimationFrame(tick);
    };

    /** The pointer left the window: let go. */
    const onLeave = (event: MouseEvent): void => {
      if (event.relatedTarget !== null) return;
      pointer = null;
      target.x = 0;
      target.y = 0;
      if (frame === 0) frame = window.requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mouseout', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('mouseout', onLeave);
      window.cancelAnimationFrame(frame);
      element.style.transform = '';
    };
  }, [tier, strength, radius]);

  return (
    <span ref={ref} className={clsx('magnetic', className)}>
      {children}
    </span>
  );
}
